
import { Link } from 'react-router-dom';

interface TrackingEmptyProps {
  type: 'not-found' | 'error';
  onReset: () => void;
}

/**
 * TrackingEmpty component displays a friendly message when no tracking
 * information is found or when an error occurs.
 *
 * Props
 * -----
 * - type: Determines which message/icon to show. Accepts `'not-found'` or `'error'`.
 * - onReset: Callback invoked when the user clicks the “Réessayer” button.
 *
 * The component validates the `type` prop to guard against unexpected values
 * (e.g., when the prop is supplied from an external source). This prevents
 * runtime crashes caused by undefined icon classes or message strings.
 */
export default function TrackingEmpty({ type, onReset }: TrackingEmptyProps) {
  // Defensive fallback: treat any unexpected value as 'error'
  const safeType = type === 'not-found' ? 'not-found' : 'error';

  return (
    <section className="max-w-lg mx-auto px-6 py-16 text-center">
      <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-6">
        <i
          className={`${
            safeType === 'not-found'
              ? 'ri-file-search-line'
              : 'ri-error-warning-line'
          } text-3xl text-gray-300`}
          aria-hidden="true"
        ></i>
      </div>

      <h3 className="text-xl font-serif text-gray-900 mb-2">
        {safeType === 'not-found' ? 'Aucune commande trouvée' : 'Une erreur est survenue'}
      </h3>

      <p className="text-sm text-gray-500 mb-8 max-w-sm mx-auto leading-relaxed">
        {safeType === 'not-found'
          ? "Nous n'avons trouvé aucune commande correspondant à votre recherche. Vérifiez votre numéro de commande ou votre adresse email et réessayez."
          : "Nous avons rencontré une erreur lors de la recherche de votre commande. Veuillez réessayer dans un instant."}
      </p>

      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={onReset}
          className="px-6 py-3 bg-[#2E7D32] text-white text-sm font-semibold rounded-xl hover:bg-[#1B5E20] transition-colors cursor-pointer whitespace-nowrap"
        >
          Réessayer
        </button>

        <Link
          to="/products"
          className="px-6 py-3 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          Voir les produits
        </Link>
      </div>
    </section>
  );
}
